// TeacherManagementDashboard.js
import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { toast } from 'react-hot-toast';
import { ActivityLineChart, StudentBarChart, TimeActivityChart } from '../Charts';

const TeacherManagementDashboard = () => {
  const [students, setStudents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [images, setImages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('students');
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 병렬로 모든 데이터 요청
      const [studentsResponse, conversationsResponse, imagesResponse, analyticsResponse] = await Promise.allSettled([
        apiClient.get('/teacher-monitoring/students'),
        apiClient.get('/teacher-monitoring/chat-history'),
        apiClient.get('/teacher-monitoring/image-history'),
        apiClient.get('/teacher-monitoring/analytics')
      ]);

      // 학생 목록 처리
      if (studentsResponse.status === 'fulfilled' && studentsResponse.value.data.success) {
        setStudents(studentsResponse.value.data.students || []);
      } else {
        console.error('학생 목록 로딩 실패:', studentsResponse.reason);
        toast.error('학생 목록을 불러오는데 실패했습니다.');
      }

      // 대화 기록 처리
      if (conversationsResponse.status === 'fulfilled' && conversationsResponse.value.data.success) {
        setConversations(conversationsResponse.value.data.sessions || []);
      } else {
        console.error('대화 기록 로딩 실패:', conversationsResponse.reason);
        toast.error('대화 기록을 불러오는데 실패했습니다.');
      }

      // 이미지 생성 기록 처리
      if (imagesResponse.status === 'fulfilled' && imagesResponse.value.data.success) {
        setImages(imagesResponse.value.data.images || []);
      } else {
        console.error('이미지 기록 로딩 실패:', imagesResponse.reason);
        toast.error('이미지 기록을 불러오는데 실패했습니다.');
      }

      // 분석 데이터 처리
      if (analyticsResponse.status === 'fulfilled' && analyticsResponse.value.data.success) {
        setAnalytics(analyticsResponse.value.data.analytics);
      } else {
        console.error('분석 데이터 로딩 실패:', analyticsResponse.reason);
      }

    } catch (error) {
      console.error('데이터 로딩 중 예상치 못한 오류:', error);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
      toast.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockContent = async (contentId, type) => {
    try {
      await apiClient.post(`/teacher/block-content`, {
        contentId,
        type
      });
      toast.success('콘텐츠가 차단되었습니다.');
      fetchStudentData();
    } catch (error) {
      toast.error('차단 처리에 실패했습니다.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const extractGradeFromLevel = (gradeLevel) => {
    if (!gradeLevel) return null;
    if (typeof gradeLevel === 'number') return gradeLevel;

    // "3학년", "3", "3rd" 등에서 숫자 추출
    const match = gradeLevel.toString().match(/(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  // 학생별 비교 차트 데이터 생성
  const getStudentComparisonData = () => {
    if (!students || students.length === 0) return [];
    return students
      .slice(0, 10) // 상위 10명만
      .map(s => ({
        name: s.name || s.username,
        messages: s.totalSessions || 0,
        images: s.totalImages || 0
      }))
      .sort((a, b) => (b.messages + b.images) - (a.messages + a.images));
  };

  // 시간대별 활동 데이터 (analytics에서 가져오거나 기본값)
  const getHourlyActivityData = () => {
    if (analytics?.hourlyActivity) {
      return analytics.hourlyActivity;
    }
    // 기본 데이터 (실제 데이터가 없을 때)
    return Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}시`,
      activity: 0
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">학생 활동 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 text-6xl mb-4">!</div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">데이터 로딩 오류</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchStudentData}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              학생 활동 관리 대시보드
            </h1>
            <p className="text-gray-600">
              우리 학교 학생들의 AI 채팅 및 이미지 생성 활동을 모니터링하고 관리할 수 있습니다.
            </p>
          </div>

          {/* 탭 네비게이션 */}
          <div className="flex flex-wrap space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('students')}
              className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'students'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              학생 현황 ({students.length})
            </button>
            <button
              onClick={() => setActiveTab('conversations')}
              className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'conversations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              대화 기록 ({conversations.length})
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'images'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              이미지 생성 ({images.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              통계 분석
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* 학생 현황 탭 */}
          {activeTab === 'students' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {students.map((student) => (
                  <div key={student.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{student.name || student.username}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        (student.grade || extractGradeFromLevel(student.gradeLevel) || 4) <= 3
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {student.grade || extractGradeFromLevel(student.gradeLevel) || '미설정'}학년
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>대화: {student.totalSessions || 0}회</p>
                      <p>이미지: {student.totalImages || 0}개</p>
                      <p>최근 활동: {student.lastActivity ? formatDate(student.lastActivity) : '없음'}</p>
                    </div>
                    <button
                      onClick={() => setSelectedStudent(student)}
                      className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      상세 보기
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 대화 기록 탭 */}
          {activeTab === 'conversations' && (
            <div className="space-y-4">
              {conversations.map((conversation) => (
                <div key={conversation.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{conversation.student?.name || conversation.student?.username}</h4>
                      <p className="text-sm text-gray-600">{formatDate(conversation.updatedAt)}</p>
                      <p className="text-sm text-gray-500">세션: {conversation.title}</p>
                      <p className="text-sm text-gray-400">{conversation.student?.grade}학년 ({conversation.student?.gradeLevel})</p>
                    </div>
                    <div className="flex space-x-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        {conversation.totalMessages}개 메시지
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                        {conversation.duration}분
                      </span>
                      <button
                        onClick={() => handleBlockContent(conversation.id, 'conversation')}
                        className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs hover:bg-red-200"
                      >
                        차단
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-600 mb-2"><strong>최근 대화 내용:</strong></p>
                    {conversation.recentMessages && conversation.recentMessages.slice(0, 2).map((msg, idx) => (
                      <div key={idx} className="text-xs text-gray-600 mb-1">
                        <strong>{msg.sender === 'user' ? '학생' : 'AI'}:</strong> {msg.message?.slice(0, 100)}...
                      </div>
                    ))}
                    {conversation.topics && conversation.topics.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1"><strong>주제:</strong></p>
                        <div className="flex flex-wrap gap-1">
                          {conversation.topics.map((topic, idx) => (
                            <span key={idx} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 이미지 생성 탭 */}
          {activeTab === 'images' && (
            <>
              {images.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="text-6xl mb-4">*</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    아직 학생들이 생성한 이미지가 없습니다
                  </h3>
                  <p className="text-sm text-gray-500">
                    학생들이 AI 이미지 생성 기능을 사용하면 여기에 표시됩니다
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {images.map((image) => (
                <div key={image.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{image.student?.name || image.student?.username}</h4>
                      <p className="text-sm text-gray-600">{formatDate(image.createdAt)}</p>
                      <p className="text-sm text-gray-400">{image.student?.grade}학년 ({image.student?.gradeLevel})</p>
                    </div>
                    <div className="flex flex-col space-y-1">
                      {image.category && (
                        <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs text-center">
                          {image.category}
                        </span>
                      )}
                      <button
                        onClick={() => handleBlockContent(image.id, 'image')}
                        className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs hover:bg-red-200"
                      >
                        차단
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>프롬프트:</strong> {image.prompt}
                    </p>
                    {image.metadata?.educationalPrompt && (
                      <p className="text-xs text-gray-600 mb-2">
                        <strong>교육적 프롬프트:</strong> {image.metadata.educationalPrompt}
                      </p>
                    )}
                    {image.metadata?.isPlaceholder && (
                      <div className="mb-2">
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                          플레이스홀더 이미지
                        </span>
                      </div>
                    )}
                    <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden">
                      {image.imageUrl ? (
                        <img
                          src={image.imageUrl}
                          alt="생성된 이미지"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('이미지 로드 실패:', image.imageUrl);
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm" style={{display: image.imageUrl ? 'none' : 'flex'}}>
                        {image.imageUrl ? '이미지 로드 실패' : '이미지 없음'}
                      </div>
                    </div>
                  </div>
                </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* 통계 분석 탭 */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* 요약 카드 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-600">
                    {analytics?.totalStudents || students.length}
                  </div>
                  <div className="text-sm text-blue-800">전체 학생</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-green-600">
                    {analytics?.activeStudents || students.filter(s => s.totalSessions > 0).length}
                  </div>
                  <div className="text-sm text-green-800">활성 학생</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-600">
                    {analytics?.totalSessions || conversations.length}
                  </div>
                  <div className="text-sm text-purple-800">총 대화 세션</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-orange-600">
                    {analytics?.totalImages || images.length}
                  </div>
                  <div className="text-sm text-orange-800">총 이미지 생성</div>
                </div>
              </div>

              {/* 일별 활동 트렌드 차트 */}
              <ActivityLineChart
                data={analytics?.dailyActivity || []}
                title="최근 30일 활동 트렌드"
              />

              {/* 학생별 비교 및 시간대별 활동 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <StudentBarChart
                  data={getStudentComparisonData()}
                  title="학생별 활동량 비교 (상위 10명)"
                />
                <TimeActivityChart
                  data={getHourlyActivityData()}
                  title="시간대별 활동 분포"
                />
              </div>

              {/* 추가 통계 정보 */}
              {analytics && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">상세 통계</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">평균 대화 수/학생</span>
                      <div className="text-lg font-semibold text-gray-800">
                        {analytics.avgSessionsPerStudent?.toFixed(1) || '-'}회
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">평균 이미지/학생</span>
                      <div className="text-lg font-semibold text-gray-800">
                        {analytics.avgImagesPerStudent?.toFixed(1) || '-'}개
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">총 메시지</span>
                      <div className="text-lg font-semibold text-gray-800">
                        {analytics.totalMessages?.toLocaleString() || '-'}개
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">활성율</span>
                      <div className="text-lg font-semibold text-gray-800">
                        {analytics.totalStudents > 0
                          ? ((analytics.activeStudents / analytics.totalStudents) * 100).toFixed(0)
                          : 0}%
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 학생 상세 모달 */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold">{selectedStudent.username} 상세 정보</h3>
              <button
                onClick={() => setSelectedStudent(null)}
                className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl"
              >
                X
              </button>
            </div>
            <div className="space-y-3">
              <p><strong>학년:</strong> {selectedStudent.grade || extractGradeFromLevel(selectedStudent.gradeLevel) || '미설정'}학년</p>
              <p><strong>이메일:</strong> {selectedStudent.email || '미설정'}</p>
              <p><strong>가입일:</strong> {formatDate(selectedStudent.created_at || selectedStudent.joinDate)}</p>
              <p><strong>총 대화 수:</strong> {selectedStudent.totalSessions || 0}회</p>
              <p><strong>총 이미지 생성:</strong> {selectedStudent.totalImages || 0}개</p>
              <p><strong>최근 활동:</strong> {selectedStudent.lastActivity ? formatDate(selectedStudent.lastActivity) : '없음'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherManagementDashboard;
